import type { ConfluenceUpdateImageOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';

import type { PassThroughCommandProps } from '../command-handlers/command-handler';

export class DocumentImageUpdateCommandProcessor {
	private onUploadAndInsertMedia?: PassThroughCommandProps['onUploadAndInsertMedia'];
	private onPrepareImageUpdate?: PassThroughCommandProps['onPrepareImageUpdate'];
	private onClearAIGenerating?: PassThroughCommandProps['onClearAIGenerating'];
	private hasPrepared = false;
	private lastPreviousMediaFileId?: string;

	constructor(
		onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia'],
		onPrepareImageUpdate?: PassThroughCommandProps['onPrepareImageUpdate'],
		onClearAIGenerating?: PassThroughCommandProps['onClearAIGenerating'],
	) {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
		this.onPrepareImageUpdate = onPrepareImageUpdate;
		this.onClearAIGenerating = onClearAIGenerating;
	}

	updateCallback(onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia']): void {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
	}

	updatePrepareCallback(
		onPrepareImageUpdate: PassThroughCommandProps['onPrepareImageUpdate'],
	): void {
		this.onPrepareImageUpdate = onPrepareImageUpdate;
	}

	updateClearCallback(onClearAIGenerating: PassThroughCommandProps['onClearAIGenerating']): void {
		this.onClearAIGenerating = onClearAIGenerating;
	}

	streamChunk(
		data: ConfluenceUpdateImageOnPageData,
		isFinished: boolean,
		_traceId?: string,
		_singleInstrumentationId?: string,
	): void {
		// If the stream errored, clear the AI generating state and bail out
		if (
			'__isError' in data &&
			(data as ConfluenceUpdateImageOnPageData & { __isError?: boolean }).__isError
		) {
			this.streamError();
			return;
		}

		// On the first chunk, if there's a previousMediaFileId, trigger the loading
		// state on the existing image immediately (before the upload starts)
		if (!this.hasPrepared && data.previousMediaFileId) {
			this.hasPrepared = true;
			this.lastPreviousMediaFileId = data.previousMediaFileId;
			this.onPrepareImageUpdate?.(data.previousMediaFileId);
		}

		if (isFinished) {
			this.hasPrepared = false;
			this.lastPreviousMediaFileId = undefined;
			void this.onUploadAndInsertMedia?.(data.mediaUrl, data);
		}
	}

	/**
	 * Called when the SSE stream errors (ACTION_ERROR) to clear the AI generating
	 * state from the existing image node.
	 */
	streamError(): void {
		if (this.lastPreviousMediaFileId) {
			this.onClearAIGenerating?.(this.lastPreviousMediaFileId);
			this.lastPreviousMediaFileId = undefined;
		}
		this.hasPrepared = false;
	}

	/**
	 * Called on abort — only clears AI generating state if an ACTION_PREPARE
	 * was received (hasPrepared is true). This prevents spurious clearing
	 * during stream resets while still handling user-initiated cancels.
	 */
	abortIfPrepared(): void {
		if (this.hasPrepared) {
			this.streamError();
		}
	}
}
